/* FarmorCorgi — Disney-style corgi grandma with glasses. FaceTime-inspired call UI.
 * Sofia can "ring" her for auditory reminders (Swedish speechSynthesis) — keys, wallet,
 * kids' gym class, work meeting outfits, etc. Persona: warm, glasses-wearing corgi grandma. */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Phone, PhoneOff, Volume2, VolumeX, X, Plus, Trash2, Sparkles, Check, PhoneCall, Sunrise, Moon, Wind, MessageCircle, Bell, BellOff, RefreshCw,
} from 'lucide-react';
import InstallPrompt from './InstallPrompt';
import { subscribePush } from '../utils/pushSubscribe';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AVATAR_URL = `${API}/farmor-corgi/avatar.png`;

const CALL_MODES = [
  { id: 'leaving',  label: 'Ska iväg',         emoji: '🚪', desc: 'Snabb-check innan du går', icon: Wind },
  { id: 'evening',  label: 'Kvälls-koll',      emoji: '🌙', desc: 'Vad har du imorgon?',      icon: Moon },
  { id: 'morning',  label: 'Morgon-brief',     emoji: '☀️', desc: 'Vad väntar idag?',         icon: Sunrise },
  { id: 'stressed', label: 'Behöver lugn',     emoji: '💜', desc: '2 andetag + samtal',       icon: Sparkles },
  { id: 'freeform', label: 'Bara prata',       emoji: '💬', desc: 'Berätta vad som helst',    icon: MessageCircle },
];

const REMINDER_KINDS = [
  { id: 'family',  label: 'Familj',   emoji: '👨‍👩‍👧' },
  { id: 'work',    label: 'Jobb',     emoji: '💼' },
  { id: 'errand',  label: 'Ärende',   emoji: '🛒' },
  { id: 'daily',   label: 'Vardag',   emoji: '📆' },
];

const WEEKDAYS = [
  { id: '',          label: 'Varje dag' },
  { id: 'monday',    label: 'Måndag' },
  { id: 'tuesday',   label: 'Tisdag' },
  { id: 'wednesday', label: 'Onsdag' },
  { id: 'thursday',  label: 'Torsdag' },
  { id: 'friday',    label: 'Fredag' },
  { id: 'saturday',  label: 'Lördag' },
  { id: 'sunday',    label: 'Söndag' },
];

// Try to pick a Swedish voice from the browser
function pickSwedishVoice() {
  try {
    const voices = window.speechSynthesis?.getVoices() || [];
    return voices.find((v) => v.lang?.startsWith('sv')) || voices.find((v) => v.lang?.startsWith('en')) || null;
  } catch { return null; }
}

function speak(text, voice) {
  try {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = 'sv-SE';
    u.rate = 0.94;
    u.pitch = 1.15;   // higher pitch for warmer grandma feel
    if (voice) u.voice = voice;
    window.speechSynthesis.speak(u);
    return u;
  } catch { /* silent */ }
}

// Real illustrated Farmor Corgi (generated once via Gemini Nano Banana + cached on backend).
// Falls back to SVG if image can't load.
function CorgiAvatar({ speaking, size = 200 }) {
  const [imgFailed, setImgFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);

  if (!imgFailed) {
    return (
      <div
        style={{ width: size, height: size, filter: 'drop-shadow(0 8px 24px rgba(190, 100, 60, 0.35))' }}
        className={`relative rounded-full overflow-hidden bg-gradient-to-br from-amber-100 to-rose-100 ${speaking ? 'animate-[corgiWobble_1.2s_ease-in-out_infinite]' : ''}`}
      >
        {!loaded && (
          <div className="absolute inset-0 flex items-center justify-center text-amber-500">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        )}
        <img
          src={AVATAR_URL}
          alt="Farmor Corgi"
          onLoad={() => setLoaded(true)}
          onError={() => setImgFailed(true)}
          className={`w-full h-full object-cover transition-opacity ${loaded ? 'opacity-100' : 'opacity-0'}`}
          data-testid="farmor-corgi-avatar-img"
        />
        <style>{`
          @keyframes corgiWobble {
            0%, 100% { transform: rotate(-2deg); }
            50% { transform: rotate(2deg); }
          }
        `}</style>
      </div>
    );
  }
  return <CorgiAvatarSVG speaking={speaking} size={size} />;
}

function CorgiAvatarSVG({ speaking, size = 200 }) {
  return (
    <svg
      viewBox="0 0 200 200"
      width={size}
      height={size}
      className={speaking ? 'animate-[corgiWobble_1.2s_ease-in-out_infinite]' : ''}
      style={{ filter: 'drop-shadow(0 8px 24px rgba(190, 100, 60, 0.35))' }}
    >
      <defs>
        <radialGradient id="cface" cx="0.5" cy="0.45" r="0.6">
          <stop offset="0%" stopColor="#FFE8CC" />
          <stop offset="60%" stopColor="#F5C79A" />
          <stop offset="100%" stopColor="#D89162" />
        </radialGradient>
        <radialGradient id="cbelly" cx="0.5" cy="0.4" r="0.6">
          <stop offset="0%" stopColor="#FFFAF0" />
          <stop offset="100%" stopColor="#F5D6B4" />
        </radialGradient>
      </defs>
      {/* Left ear */}
      <path d="M40 55 Q35 15 65 30 L75 60 Z" fill="#B87344" stroke="#8B4E24" strokeWidth="1.5" />
      <path d="M50 45 Q52 30 62 38 L68 55 Z" fill="#FFD9B0" />
      {/* Right ear */}
      <path d="M160 55 Q165 15 135 30 L125 60 Z" fill="#B87344" stroke="#8B4E24" strokeWidth="1.5" />
      <path d="M150 45 Q148 30 138 38 L132 55 Z" fill="#FFD9B0" />
      {/* Head */}
      <ellipse cx="100" cy="105" rx="70" ry="65" fill="url(#cface)" stroke="#8B4E24" strokeWidth="2" />
      {/* Belly / chest fluff */}
      <ellipse cx="100" cy="140" rx="40" ry="28" fill="url(#cbelly)" />
      {/* Cheek blush */}
      <circle cx="55" cy="125" r="10" fill="#FFB3C6" opacity="0.5" />
      <circle cx="145" cy="125" r="10" fill="#FFB3C6" opacity="0.5" />
      {/* Muzzle */}
      <ellipse cx="100" cy="125" rx="28" ry="20" fill="#FFF3E0" stroke="#8B4E24" strokeWidth="1.2" />
      {/* Nose */}
      <ellipse cx="100" cy="115" rx="7" ry="5" fill="#3A2418" />
      <ellipse cx="98" cy="113" rx="2" ry="1.5" fill="#fff" opacity="0.8" />
      {/* Mouth */}
      {speaking ? (
        <ellipse cx="100" cy="138" rx="6" ry="7" fill="#3A2418" />
      ) : (
        <path d="M92 133 Q100 142 108 133" stroke="#3A2418" strokeWidth="2.2" fill="none" strokeLinecap="round" />
      )}
      {/* Tongue peek */}
      {speaking && <ellipse cx="100" cy="141" rx="3" ry="2" fill="#FF8FA3" />}
      {/* Glasses — round Disney style */}
      <g stroke="#2A2A2A" strokeWidth="2.8" fill="none">
        <circle cx="72" cy="95" r="17" fill="#EAF4FF" fillOpacity="0.55" />
        <circle cx="128" cy="95" r="17" fill="#EAF4FF" fillOpacity="0.55" />
        <line x1="89" y1="95" x2="111" y2="95" />
        <line x1="55" y1="88" x2="42" y2="83" />
        <line x1="145" y1="88" x2="158" y2="83" />
      </g>
      {/* Eyes behind glasses */}
      <g fill="#3A2418">
        <ellipse cx="72" cy="97" rx="4" ry={speaking ? 3.5 : 4.5} />
        <ellipse cx="128" cy="97" rx="4" ry={speaking ? 3.5 : 4.5} />
        <circle cx="73.5" cy="95.5" r="1.5" fill="#fff" />
        <circle cx="129.5" cy="95.5" r="1.5" fill="#fff" />
      </g>
      {/* Eyebrow tufts */}
      <path d="M60 78 Q68 74 82 79" stroke="#8B4E24" strokeWidth="2.5" fill="none" strokeLinecap="round" />
      <path d="M140 78 Q132 74 118 79" stroke="#8B4E24" strokeWidth="2.5" fill="none" strokeLinecap="round" />
      <style>{`
        @keyframes corgiWobble {
          0%, 100% { transform: rotate(-2deg); }
          50% { transform: rotate(2deg); }
        }
      `}</style>
    </svg>
  );
}

function NotificationSettings() {
  const [permission, setPermission] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'default');
  const [subscribed, setSubscribed] = useState(false);
  const [times, setTimes] = useState({ morning: '07:30', evening: '20:30', enabled: true });
  const [busy, setBusy] = useState(false);
  const [supported, setSupported] = useState(true);

  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || typeof Notification === 'undefined') {
      setSupported(false);
      return;
    }
    axios.get(`${API}/farmor-corgi/notifications/times`, { withCredentials: true })
      .then(({ data }) => setTimes({
        morning: data.morning || '07:30',
        evening: data.evening || '20:30',
        enabled: data.enabled !== false,
      }))
      .catch(() => {});
    // Check existing subscription
    navigator.serviceWorker.getRegistration().then((reg) => {
      if (reg) reg.pushManager.getSubscription().then((sub) => setSubscribed(!!sub));
    });
  }, []);

  const enable = async () => {
    setBusy(true);
    try {
      const perm = await subscribePush();
      setPermission(perm);
      setSubscribed(true);
      toast.success('Notiser på 💜 Farmor kan nu ringa dig');
    } catch (e) {
      toast.error(e?.message || 'Kunde inte aktivera notiser');
    } finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg && await reg.pushManager.getSubscription();
      if (sub) {
        await axios.post(`${API}/farmor-corgi/notifications/unsubscribe`,
          { endpoint: sub.endpoint, keys: sub.toJSON().keys },
          { withCredentials: true });
        await sub.unsubscribe();
      }
      setSubscribed(false);
      toast.success('Notiser av');
    } catch { toast.error('Kunde inte stänga av'); }
    finally { setBusy(false); }
  };

  const saveTimes = async () => {
    try {
      await axios.put(`${API}/farmor-corgi/notifications/times`, times, { withCredentials: true });
      toast.success('Tider sparade 💜');
    } catch { toast.error('Kunde inte spara'); }
  };

  const sendTest = async () => {
    try {
      const { data } = await axios.post(`${API}/farmor-corgi/notifications/test`, {}, { withCredentials: true });
      toast.success(`Test-notis skickad (${data.sent} enhet${data.sent !== 1 ? 'er' : ''}) 🐾`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Kunde inte skicka test'); }
  };

  if (!supported) {
    return (
      <div className="rounded-2xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900" data-testid="farmor-corgi-notif-unsupported">
        <BellOff className="w-3.5 h-3.5 inline mr-1" />
        Din webbläsare stödjer inte push-notiser. Prova via Chrome/Edge på mobil eller dator.
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-white border border-amber-100 p-4" data-testid="farmor-corgi-notif">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
          <Bell className="w-3.5 h-3.5 text-amber-500" /> Låt Farmor ringa dig — notiser
        </div>
        {subscribed && permission === 'granted' ? (
          <button
            onClick={disable} disabled={busy}
            data-testid="farmor-corgi-notif-disable"
            className="text-xs px-3 py-1 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 inline-flex items-center gap-1"
          >
            <BellOff className="w-3 h-3" /> Stäng av
          </button>
        ) : (
          <button
            onClick={enable} disabled={busy}
            data-testid="farmor-corgi-notif-enable"
            className="text-xs px-3 py-1 rounded-full bg-amber-500 hover:bg-amber-600 text-white inline-flex items-center gap-1"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bell className="w-3 h-3" />}
            Aktivera notiser
          </button>
        )}
      </div>

      {subscribed && permission === 'granted' && (
        <>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <label className="text-xs">
              <div className="text-slate-600 mb-1 flex items-center gap-1"><Sunrise className="w-3 h-3" /> Morgon</div>
              <input
                type="time"
                value={times.morning || ''}
                onChange={(e) => setTimes({ ...times, morning: e.target.value })}
                data-testid="farmor-corgi-notif-morning"
                className="w-full text-sm rounded-lg border border-slate-200 px-2 py-1"
              />
            </label>
            <label className="text-xs">
              <div className="text-slate-600 mb-1 flex items-center gap-1"><Moon className="w-3 h-3" /> Kväll</div>
              <input
                type="time"
                value={times.evening || ''}
                onChange={(e) => setTimes({ ...times, evening: e.target.value })}
                data-testid="farmor-corgi-notif-evening"
                className="w-full text-sm rounded-lg border border-slate-200 px-2 py-1"
              />
            </label>
          </div>
          <div className="flex gap-2 justify-end">
            <button
              onClick={sendTest}
              data-testid="farmor-corgi-notif-test"
              className="text-xs px-3 py-1 rounded-full bg-white border border-amber-300 text-amber-800 hover:bg-amber-50 inline-flex items-center gap-1"
            >
              <PhoneCall className="w-3 h-3" /> Skicka test
            </button>
            <button
              onClick={saveTimes}
              data-testid="farmor-corgi-notif-save"
              className="text-xs px-3 py-1 rounded-full bg-amber-500 hover:bg-amber-600 text-white font-semibold"
            >
              Spara tider
            </button>
          </div>
          <p className="text-[10px] text-slate-400 italic mt-2">
            🐾 Farmor ringer varje morgon/kväll (svensk tid) med imorgondagens saker & påminnelser.
          </p>
        </>
      )}
    </div>
  );
}

export default function FarmorCorgi() {
  const [showCall, setShowCall] = useState(false);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ kind: 'family', text: '', person: '', weekday: '', time_hint: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/farmor-corgi/reminders`, { withCredentials: true });
      setReminders(data.items || []);
    } catch { toast.error('Kunde inte ladda påminnelser'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addReminder = async () => {
    if (form.text.trim().length < 3) { toast.error('Skriv minst 3 tecken'); return; }
    try {
      await axios.post(`${API}/farmor-corgi/reminders`, form, { withCredentials: true });
      setForm({ kind: 'family', text: '', person: '', weekday: '', time_hint: '' });
      setAdding(false);
      toast.success('Sparat 💜');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Kunde inte spara'); }
  };

  const delReminder = async (id) => {
    if (!window.confirm('Ta bort påminnelsen?')) return;
    try {
      await axios.delete(`${API}/farmor-corgi/reminders/${id}`, { withCredentials: true });
      await load();
    } catch { toast.error('Kunde inte ta bort'); }
  };

  const grouped = REMINDER_KINDS.map((k) => ({
    ...k,
    items: reminders.filter((r) => r.kind === k.id),
  }));

  return (
    <div className="max-w-3xl mx-auto space-y-4" data-testid="farmor-corgi-page">
      <InstallPrompt />

      {/* Hero + big call button */}
      <div
        className="rounded-3xl p-5 border relative overflow-hidden text-center"
        style={{ background: 'linear-gradient(135deg, #FFF0E0 0%, #FFE8D6 50%, #F5D5B0 100%)', borderColor: '#E5B588' }}
      >
        <div className="flex flex-col items-center">
          <CorgiAvatar size={140} />
          <div className="mt-3 text-[11px] uppercase font-semibold tracking-widest text-amber-800/80">Farmor Corgi</div>
          <h1
            className="text-2xl font-bold text-amber-950"
            style={{ fontFamily: '"Caveat", ui-serif, Georgia, serif' }}
          >
            Ring mig när du behöver 🐾
          </h1>
          <p className="text-sm text-amber-900/80 mt-1 max-w-sm">
            Jag är en varm liten röst i din ficka — jag påminner om nycklar, plånbok,
            barnens grejer, jobbmötet imorgon…
          </p>

          <button
            type="button"
            onClick={() => setShowCall(true)}
            data-testid="farmor-corgi-call-btn"
            className="mt-4 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-sm font-bold shadow-lg hover:opacity-90 hover:scale-105 transition"
          >
            <PhoneCall className="w-4 h-4" /> Ring Farmor Corgi
          </button>
        </div>
      </div>

      {/* Notifications */}
      <NotificationSettings />

      {/* Reminders list */}
      <div className="rounded-2xl bg-white border border-amber-100 p-4" data-testid="farmor-corgi-reminders">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5 text-amber-500" /> Vad ska Farmor komma ihåg åt dig?
          </div>
          <button
            onClick={() => setAdding(true)}
            data-testid="farmor-corgi-add-btn"
            className="text-xs px-3 py-1 rounded-full bg-amber-500 hover:bg-amber-600 text-white inline-flex items-center gap-1"
          >
            <Plus className="w-3 h-3" /> Ny påminnelse
          </button>
        </div>

        {adding && (
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 mb-3 space-y-2">
            <div className="flex flex-wrap gap-1.5">
              {REMINDER_KINDS.map((k) => (
                <button
                  key={k.id}
                  onClick={() => setForm({ ...form, kind: k.id })}
                  data-testid={`farmor-corgi-kind-${k.id}`}
                  className={`text-xs px-2 py-0.5 rounded-full border ${form.kind === k.id ? 'bg-amber-500 border-amber-500 text-white' : 'bg-white border-slate-200'}`}
                >
                  {k.emoji} {k.label}
                </button>
              ))}
            </div>
            <input
              type="text"
              value={form.text}
              onChange={(e) => setForm({ ...form, text: e.target.value })}
              placeholder="T.ex. Saga har gympa på tisdagar"
              data-testid="farmor-corgi-input-text"
              className="w-full text-sm rounded-lg border border-slate-200 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-amber-200"
              autoFocus
            />
            <div className="flex flex-wrap gap-2">
              <input
                type="text"
                value={form.person}
                onChange={(e) => setForm({ ...form, person: e.target.value })}
                placeholder="Vem? (Saga, Nils, jag…)"
                className="text-xs rounded border border-slate-200 px-2 py-1 flex-1 min-w-[130px]"
              />
              <select
                value={form.weekday}
                onChange={(e) => setForm({ ...form, weekday: e.target.value })}
                className="text-xs rounded border border-slate-200 px-2 py-1"
                data-testid="farmor-corgi-weekday"
              >
                {WEEKDAYS.map((w) => <option key={w.id} value={w.id}>{w.label}</option>)}
              </select>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => { setAdding(false); setForm({ kind: 'family', text: '', person: '', weekday: '', time_hint: '' }); }}
                className="text-xs px-3 py-1 rounded-full bg-white border border-slate-200 text-slate-600">
                Avbryt
              </button>
              <button onClick={addReminder} data-testid="farmor-corgi-save"
                className="text-xs px-3 py-1 rounded-full bg-amber-500 hover:bg-amber-600 text-white font-semibold">
                Spara
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-3 text-slate-400 text-xs"><Loader2 className="w-4 h-4 animate-spin inline" /> Laddar…</div>
        ) : reminders.length === 0 ? (
          <div className="text-xs text-slate-400 italic text-center py-3">
            Inga påminnelser än. Lägg till en så vet Farmor vad du vill bli påmind om.
          </div>
        ) : (
          <div className="space-y-3">
            {grouped.filter((g) => g.items.length > 0).map((g) => (
              <div key={g.id}>
                <div className="text-[10px] uppercase font-bold tracking-wider text-slate-500 mb-1">
                  {g.emoji} {g.label}
                </div>
                <div className="space-y-1">
                  {g.items.map((r) => (
                    <div
                      key={r.id}
                      className="flex items-start gap-2 p-2 rounded-lg bg-slate-50 group"
                      data-testid={`farmor-corgi-item-${r.id}`}
                    >
                      <Check className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-slate-900">{r.text}</div>
                        <div className="text-[10px] text-slate-500 mt-0.5">
                          {r.person && <span>{r.person} · </span>}
                          {r.weekday && <span>{WEEKDAYS.find((w) => w.id === r.weekday)?.label}</span>}
                          {!r.person && !r.weekday && <span>Varje dag</span>}
                        </div>
                      </div>
                      <button
                        onClick={() => delReminder(r.id)}
                        className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500"
                        aria-label="Ta bort"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showCall && <CallModal onClose={() => setShowCall(false)} />}
    </div>
  );
}

function CallModal({ onClose }) {
  const [mode, setMode] = useState('leaving');
  const [freeformText, setFreeformText] = useState('');
  const [reply, setReply] = useState(null);
  const [quickChecks, setQuickChecks] = useState([]);
  const [checkedIds, setCheckedIds] = useState({});
  const [calling, setCalling] = useState(false);
  const [voiceOn, setVoiceOn] = useState(true);
  const [speaking, setSpeaking] = useState(false);
  const voiceRef = useRef(null);

  useEffect(() => {
    // Warm up voices
    if (window.speechSynthesis) {
      voiceRef.current = pickSwedishVoice();
      const handler = () => { voiceRef.current = pickSwedishVoice(); };
      window.speechSynthesis.addEventListener?.('voiceschanged', handler);
      return () => window.speechSynthesis.removeEventListener?.('voiceschanged', handler);
    }
  }, []);

  useEffect(() => {
    return () => { try { window.speechSynthesis?.cancel(); } catch { /* silent */ } };
  }, []);

  const doCall = async () => {
    setCalling(true);
    setReply(null);
    setQuickChecks([]);
    setCheckedIds({});
    try {
      const { data } = await axios.post(`${API}/farmor-corgi/call`,
        { mode, user_text: freeformText },
        { withCredentials: true }
      );
      setReply(data.reply);
      setQuickChecks(data.quick_checks || []);
      if (voiceOn && data.reply) {
        setSpeaking(true);
        const u = speak(data.reply, voiceRef.current);
        if (u) u.onend = () => setSpeaking(false);
        // Fallback timer to unspeak after ~15s
        setTimeout(() => setSpeaking(false), 15000);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Fick inte tag på Farmor');
    } finally { setCalling(false); }
  };

  const speakAgain = () => {
    if (!reply) return;
    setSpeaking(true);
    const u = speak(reply, voiceRef.current);
    if (u) u.onend = () => setSpeaking(false);
    setTimeout(() => setSpeaking(false), 15000);
  };

  const toggleCheck = (id) => {
    setCheckedIds((c) => ({ ...c, [id]: !c[id] }));
  };

  const saveChecks = async () => {
    const checked = Object.keys(checkedIds).filter((k) => checkedIds[k]);
    const missing = quickChecks.map((q) => q.id).filter((id) => !checkedIds[id]);
    try {
      await axios.post(`${API}/farmor-corgi/quick-check`, { checked, missing }, { withCredentials: true });
    } catch { /* silent */ }
  };

  const hangUp = async () => {
    try { window.speechSynthesis?.cancel(); } catch { /* silent */ }
    if (quickChecks.length > 0) await saveChecks();
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      data-testid="farmor-corgi-call-modal"
    >
      <div
        className="relative rounded-3xl w-full max-w-md overflow-hidden shadow-2xl"
        style={{ background: 'linear-gradient(180deg, #FFE8D0 0%, #F5CFA0 100%)' }}
      >
        {/* Top status bar — FaceTime-y */}
        <div className="flex items-center justify-between px-4 py-2 bg-black/10">
          <div className="flex items-center gap-1.5 text-amber-900 text-xs font-semibold">
            <span className={`w-2 h-2 rounded-full ${speaking ? 'bg-emerald-500 animate-pulse' : 'bg-emerald-500'}`} />
            {speaking ? 'Farmor pratar…' : reply ? 'Ansluten' : 'Ringer…'}
          </div>
          <button
            onClick={() => setVoiceOn((v) => !v)}
            className="text-amber-900"
            aria-label="Volym"
            data-testid="farmor-corgi-voice-toggle"
          >
            {voiceOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
          </button>
        </div>

        {/* Corgi hero */}
        <div className="flex flex-col items-center pt-6 pb-4 px-6">
          <CorgiAvatar size={180} speaking={speaking} />
          <div className="mt-3 text-lg font-bold text-amber-950" style={{ fontFamily: '"Caveat", ui-serif, Georgia, serif' }}>
            Farmor Corgi 🐾
          </div>

          {!reply && !calling && (
            <div className="mt-4 w-full space-y-2">
              <div className="text-xs text-amber-900/80 text-center mb-1">Vad ringer du om?</div>
              <div className="grid grid-cols-2 gap-1.5">
                {CALL_MODES.map((cm) => {
                  const Icon = cm.icon;
                  return (
                    <button
                      key={cm.id}
                      onClick={() => setMode(cm.id)}
                      data-testid={`farmor-corgi-mode-${cm.id}`}
                      className={`text-left rounded-xl p-2 border-2 transition ${
                        mode === cm.id
                          ? 'bg-white border-amber-500 shadow-sm'
                          : 'bg-white/50 border-transparent hover:bg-white/80'
                      }`}
                    >
                      <div className="flex items-center gap-1.5">
                        <Icon className="w-3.5 h-3.5 text-amber-700" />
                        <div className="text-xs font-bold text-amber-950">{cm.emoji} {cm.label}</div>
                      </div>
                      <div className="text-[10px] text-amber-800/70 mt-0.5">{cm.desc}</div>
                    </button>
                  );
                })}
              </div>
              {mode === 'freeform' && (
                <textarea
                  value={freeformText}
                  onChange={(e) => setFreeformText(e.target.value)}
                  placeholder="Berätta för Farmor…"
                  rows={2}
                  maxLength={400}
                  data-testid="farmor-corgi-freeform"
                  className="w-full text-sm rounded-lg border border-amber-300 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-amber-200 bg-white/80"
                />
              )}
            </div>
          )}

          {calling && (
            <div className="mt-4 text-sm text-amber-900 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Farmor tänker…
            </div>
          )}

          {reply && (
            <>
              <div
                className="mt-4 rounded-2xl bg-white/85 backdrop-blur border border-amber-200 p-4 w-full"
                data-testid="farmor-corgi-reply"
              >
                <p className="text-sm text-amber-950 leading-relaxed italic">{reply}</p>
                <button
                  onClick={speakAgain}
                  disabled={speaking}
                  data-testid="farmor-corgi-speak-again"
                  className="mt-2 text-[11px] px-2 py-0.5 rounded-full bg-amber-100 hover:bg-amber-200 text-amber-800 inline-flex items-center gap-1 disabled:opacity-50"
                >
                  <Volume2 className="w-3 h-3" /> Säg igen
                </button>
              </div>
              {quickChecks.length > 0 && (
                <div className="mt-3 w-full" data-testid="farmor-corgi-quickchecks">
                  <div className="text-[10px] uppercase font-bold tracking-wider text-amber-800/70 mb-1">Snabb-check</div>
                  <div className="flex flex-wrap gap-1.5">
                    {quickChecks.map((q) => {
                      const on = !!checkedIds[q.id];
                      return (
                        <button
                          key={q.id}
                          onClick={() => toggleCheck(q.id)}
                          data-testid={`farmor-corgi-check-${q.id}`}
                          className={`text-xs px-2.5 py-1 rounded-full border-2 transition ${
                            on ? 'bg-emerald-500 border-emerald-500 text-white' : 'bg-white border-amber-300 text-amber-900'
                          }`}
                        >
                          {on ? '✓ ' : ''}{q.emoji} {q.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Call actions */}
        <div className="flex justify-center gap-3 py-4 bg-black/5">
          {!reply && !calling && (
            <button
              onClick={doCall}
              data-testid="farmor-corgi-do-call"
              className="inline-flex items-center gap-1.5 px-6 py-3 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold shadow-lg"
            >
              <Phone className="w-4 h-4" /> Starta samtalet
            </button>
          )}
          <button
            onClick={hangUp}
            data-testid="farmor-corgi-hangup"
            className="inline-flex items-center gap-1.5 px-5 py-3 rounded-full bg-rose-500 hover:bg-rose-600 text-white text-sm font-bold shadow-lg"
          >
            <PhoneOff className="w-4 h-4" /> Lägg på
          </button>
        </div>

        <button
          onClick={onClose}
          className="absolute top-2 right-2 p-1.5 rounded-full bg-white/60 hover:bg-white text-amber-900"
          aria-label="Stäng"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
